import { invalidateAllResourceAudience } from "@gram/client/react-query/resourceAudience.js";
import { Page } from "@/components/page-layout";
import { RequireScope } from "@/components/require-scope";
import { AttachedUserSessions } from "@/components/sessions/AttachedUserSessions";
import { useRBAC } from "@/hooks/useRBAC";
import { useTabScrollReset } from "@/hooks/useTabScrollReset";
import { platformEndpointSlug } from "@/hooks/useToolsetUrl";
import {
  cn,
  firstPartyConnectUrl,
  getServerURL,
  supportsFirstPartyConnect,
} from "@/lib/utils";
import { getMcpServerArgs, mcpServerRouteParam } from "@/lib/sources";
import { useRoutes } from "@/routes";
import { useProjectSlugForRequests } from "@/contexts/Sdk";
import { useListToolsets } from "@gram/client/react-query/listToolsets.js";
import type {
  McpServer,
  McpServerVisibility,
} from "@gram/client/models/components/mcpserver.js";
import {
  invalidateAllGetMcpServer,
  useGetMcpServer,
} from "@gram/client/react-query/getMcpServer.js";
import { useMcpEndpoints } from "@gram/client/react-query/mcpEndpoints.js";
import { invalidateAllMcpServers } from "@gram/client/react-query/mcpServers.js";
import { useGetTunneledMcpServer } from "@gram/client/react-query/getTunneledMcpServer.js";
import { getTunneledMcpServerArgs } from "@/lib/sources";
import { invalidateAllPlugins } from "@gram/client/react-query/plugins";
import { invalidateAllPublishStatus } from "@gram/client/react-query/publishStatus";
import { useUpdateMcpServerMutation } from "@gram/client/react-query/updateMcpServer.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { Switch } from "@/components/ui/Switch";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { Link, Navigate, useLocation, useParams } from "react-router";
import { toast } from "sonner";
import { MCPTeamAccessTab } from "../MCPTeamAccessTab";
import {
  activeTabFromPath,
  initialTabFromHash,
  isCanonicalHostedWrapper,
  isLegacyAuthenticationTabPath,
  isLegacyToolsTabPath,
  mcpServerTabHref,
  MCP_SERVER_TAB_URLS,
  toolsetTabForServerPath,
} from "./MCPServerDetailsRouting";
import { mcpDetailTabHref } from "../MCPDetailsRouting";
import { InspectTab } from "./tabs/InspectTab";
import { OverviewTab } from "./tabs/OverviewTab";
import { MCP_AGENT_SETUP_SECTION_ID } from "./tabs/settings/sections/AgentSetupSection";
import { MCP_AUTHENTICATION_SECTION_ID } from "./tabs/settings/sections/authentication/AuthenticationSection";
import { MCP_PUBLIC_ACCESS_SECTION_ID } from "./tabs/settings/sections/PublicAccessSection";
import { ClientsAndSessionsTab } from "@/components/sessions/ClientsAndSessionsTab";
import { SettingsTab } from "./tabs/settings/SettingsTab";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { GuardrailsTab } from "./tabs/guardrails/GuardrailsTab";

export default function MCPServerDetails(): JSX.Element {
  const { mcpServerSlug } = useParams<{ mcpServerSlug: string }>();
  const location = useLocation();
  const routes = useRoutes();
  const mcpScopedFlag = useFeatureFlag(FEATURE_FLAGS.mcpScopedPolicies);
  const mcpScoped = mcpScopedFlag.status === "enabled";
  const idOrSlug = mcpServerSlug ?? "";
  const activeTab = activeTabFromPath(location.pathname, idOrSlug);
  const tabContentRef = useTabScrollReset(activeTab);
  const legacyAuthenticationPath = isLegacyAuthenticationTabPath(
    location.pathname,
    idOrSlug,
  );
  const legacyToolsPath = isLegacyToolsTabPath(location.pathname, idOrSlug);

  const {
    data: mcpServer,
    isLoading,
    isError,
  } = useGetMcpServer(getMcpServerArgs(idOrSlug), undefined, {
    enabled: idOrSlug !== "",
  });

  const mcpServerId = mcpServer?.id ?? "";

  const toolsetTab = toolsetTabForServerPath(
    location.pathname,
    idOrSlug,
    location.hash,
  );
  const canonicalWrapper =
    mcpServer !== undefined &&
    toolsetTab !== undefined &&
    isCanonicalHostedWrapper(mcpServer);
  const gramProject = useProjectSlugForRequests();
  const { data: toolsetsResult, isLoading: isLoadingToolsets } =
    useListToolsets({ gramProject }, undefined, {
      enabled: canonicalWrapper,
      throwOnError: false,
    });
  const wrappedToolsetSlug = canonicalWrapper
    ? toolsetsResult?.toolsets.find((t) => t.id === mcpServer?.toolsetId)?.slug
    : undefined;

  const { data: endpointsResult, isLoading: isLoadingEndpoints } =
    useMcpEndpoints({ mcpServerId }, undefined, {
      enabled: mcpServerId !== "",
    });
  const endpoints = endpointsResult?.mcpEndpoints ?? [];
  const platformSlug = platformEndpointSlug(endpoints);
  const platformConnectUrl = supportsFirstPartyConnect({
    userSessionIssuerId: mcpServer?.userSessionIssuerId,
    tunneledMcpServerId: mcpServer?.tunneledMcpServerId,
    visibility: mcpServer?.visibility,
    platformSlug,
  })
    ? firstPartyConnectUrl(
        platformSlug ? `${getServerURL()}/mcp/${platformSlug}` : undefined,
      )
    : undefined;

  if (!idOrSlug) {
    return <Navigate to={routes.mcp.href()} replace />;
  }
  if (isError || (!isLoading && !mcpServer)) {
    return <Navigate to={routes.mcp.href()} replace />;
  }
  if (wrappedToolsetSlug && toolsetTab) {
    return (
      <Navigate
        to={mcpDetailTabHref(routes, wrappedToolsetSlug, toolsetTab)}
        replace
      />
    );
  }
  if (legacyAuthenticationPath) {
    return (
      <Navigate
        to={`${mcpServerTabHref(routes, idOrSlug, "settings")}#${MCP_AUTHENTICATION_SECTION_ID}`}
        replace
      />
    );
  }
  if (legacyToolsPath) {
    return (
      <Navigate to={mcpServerTabHref(routes, idOrSlug, "inspect")} replace />
    );
  }
  // Inspect and Clients and Sessions are hidden from the nav for unproxied
  // servers (see McpServerXSidebarNav) but the routes still resolve, so bounce
  // anyone who lands on them directly (old links, the legacy /tools redirect
  // above) back to Overview instead of rendering a tab with nothing to show.
  if (
    (activeTab === "inspect" || activeTab === "sessions") &&
    mcpServer?.unproxiedMcpServerId
  ) {
    return (
      <Navigate to={mcpServerTabHref(routes, idOrSlug, "overview")} replace />
    );
  }
  if (!activeTab) {
    const initialTab = initialTabFromHash(location.hash);
    const hash =
      location.hash === `#${MCP_AUTHENTICATION_SECTION_ID}`
        ? `#${MCP_AUTHENTICATION_SECTION_ID}`
        : "";

    return (
      <Navigate
        to={`${mcpServerTabHref(routes, idOrSlug, initialTab)}${hash}`}
        replace
      />
    );
  }
  const renderTabContent = () => {
    if (canonicalWrapper && isLoadingToolsets) return null;
    switch (activeTab) {
      case "overview":
        return (
          mcpServer && (
            <OverviewTab
              mcpServer={mcpServer}
              agentSetupHref={`${mcpServerTabHref(routes, idOrSlug, "settings")}#${MCP_AGENT_SETUP_SECTION_ID}`}
            />
          )
        );
      case "inspect":
        return (
          mcpServer && (
            <InspectTab
              mcpServer={mcpServer}
              endpoints={endpoints}
              isLoadingEndpoints={isLoadingEndpoints}
            />
          )
        );
      case "team-access":
        return (
          mcpServer && (
            <RequireScope scope="org:read" level="page">
              <RequireScope
                scope="mcp:read"
                resourceId={mcpServer.id}
                level="page"
              >
                {/* mcp_servers-backed servers grant under the same `mcp:*`
                  scope kind as toolset-backed ones (see selector.go), so
                  MCPTeamAccessTab is reused as-is with the mcp_server's
                  id as the resource id. No `tools` prop because the
                  Remote MCP backend doesn't expose a Speakeasy-side tool
                  catalog. */}
                <MCPTeamAccessTab
                  resourceId={mcpServer.id}
                  serverName={mcpServer.name ?? undefined}
                />
              </RequireScope>
            </RequireScope>
          )
        );
      case "guardrails":
        // Wait out the flag lookup rather than bouncing a deep link to it.
        if (mcpScopedFlag.status === "loading") return null;
        return mcpServer && mcpScoped && !mcpServer.unproxiedMcpServerId ? (
          <GuardrailsTab mcpServer={mcpServer} />
        ) : (
          mcpServer && (
            <Navigate
              to={mcpServerTabHref(routes, idOrSlug, "overview")}
              replace
            />
          )
        );
      case "sessions":
        return (
          mcpServer && (
            <RequireScope scope="project:read" level="page">
              <ClientsAndSessionsTab
                issuerId={mcpServer.userSessionIssuerId}
                originatingMcpServerId={mcpServer.id}
                attachedSessions={
                  mcpServer.userSessionIssuerId ? (
                    <AttachedUserSessions
                      issuerId={mcpServer.userSessionIssuerId}
                      connectUrl={platformConnectUrl}
                    />
                  ) : undefined
                }
              />
            </RequireScope>
          )
        );
      case "settings":
        return (
          mcpServer && (
            <SettingsTab
              mcpServer={mcpServer}
              endpoints={endpoints}
              isLoadingEndpoints={isLoadingEndpoints}
            />
          )
        );
    }
  };

  return (
    <Page>
      <Page.Header>
        <Page.Header.Breadcrumbs
          substitutions={{
            [idOrSlug]: mcpServer?.name || "MCP Server",
          }}
          skipSegments={[
            "x",
            // skipSegments matches by literal value, not position — if the
            // server's own slug happens to collide with a tab name (e.g. a
            // server slugged "settings"), guard against also skipping the
            // server's own breadcrumb crumb.
            ...MCP_SERVER_TAB_URLS.filter((tab) => tab !== idOrSlug),
          ]}
        />
      </Page.Header>

      <Page.Body fullWidth className="gap-0">
        <div
          ref={tabContentRef}
          className="mx-auto w-full max-w-[1270px] flex-1"
        >
          {renderTabContent()}
        </div>
      </Page.Body>
    </Page>
  );
}

type VisibilityOption = {
  value: McpServerVisibility;
  label: string;
  description: string;
  dotClass: string;
  hoverDotClass: string;
};

const VISIBILITY_OPTIONS: VisibilityOption[] = [
  {
    value: "disabled",
    label: "Disabled",
    description: "This server is offline. No users can connect to it",
    dotClass: "bg-amber-400",
    hoverDotClass: "group-hover:bg-amber-400",
  },
  {
    value: "private",
    label: "Private",
    description: "The server serves traffic.",
    dotClass: "bg-blue-400",
    hoverDotClass: "group-hover:bg-blue-400",
  },
];

// Public visibility is only offered for tunneled-backed servers, and only
// once the tunnel source owner has consented (double opt-in).
const PUBLIC_VISIBILITY_OPTION: VisibilityOption = {
  value: "public",
  label: "Public",
  description:
    "Anyone can connect anonymously — no login. Every tool is exposed to the public internet.",
  dotClass: "bg-green-400",
  hoverDotClass: "group-hover:bg-green-400",
};

export function MCPServerStatusDropdown({
  server,
}: {
  server: McpServer;
}): JSX.Element {
  const { canWrite, updateVisibility, updating } =
    useMcpServerVisibilityUpdate(server);
  const isTunneled = Boolean(server.tunneledMcpServerId);
  const { data: tunneledSource } = useGetTunneledMcpServer(
    getTunneledMcpServerArgs(server.tunneledMcpServerId ?? ""),
    undefined,
    { enabled: isTunneled },
  );
  const sourceAllowsPublic = tunneledSource?.allowPublic ?? false;
  const options = isTunneled
    ? [...VISIBILITY_OPTIONS, PUBLIC_VISIBILITY_OPTION]
    : VISIBILITY_OPTIONS;
  const currentLabel = visibilityLabel(server.visibility);
  const currentDotClass =
    options.find((option) => option.value === server.visibility)?.dotClass ??
    "bg-green-400";
  // Public is gated on the source's consent; the option stays visible but
  // disabled, and this footer takes the owner to where consent is given.
  const publicBlocked = isTunneled && !sourceAllowsPublic;
  const routes = useRoutes();
  const publicAccessHref = `${mcpServerTabHref(routes, mcpServerRouteParam(server), "settings")}#${MCP_PUBLIC_ACCESS_SECTION_ID}`;

  // Unproxied servers have no Speakeasy-hosted endpoint for disabled/private to
  // gate — the vendor's own server is reachable regardless of this setting —
  // so there's nothing to toggle. Still show the record's actual stored
  // value (not a hardcoded "Public") so this can't drift from what Settings
  // and the readiness checklist report for the same server.
  if (server.unproxiedMcpServerId) {
    return (
      <span className="text-foreground border-border flex w-fit items-center gap-2 border px-3 py-1.5 text-sm font-medium">
        <span
          className={cn("h-2 w-2 shrink-0 rounded-full", currentDotClass)}
        />
        {currentLabel}
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={!canWrite || updating}>
        <button
          type="button"
          disabled={!canWrite || updating}
          className="text-foreground hover:bg-muted trans border-border flex w-fit items-center gap-2 border px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span
            className={cn("h-2 w-2 shrink-0 rounded-full", currentDotClass)}
          />
          {currentLabel}
          <ChevronDown className="text-muted-foreground h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[320px] p-1">
        {options.map((option) => {
          const optionBlocked = option.value === "public" && publicBlocked;
          return (
            <DropdownMenuItem
              key={option.value}
              disabled={optionBlocked}
              onSelect={() => {
                if (optionBlocked) return;
                updateVisibility(option.value);
              }}
              className="group flex cursor-pointer items-start gap-2.5 p-2 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60"
            >
              {option.value === server.visibility ? (
                <span
                  className={cn(
                    "mt-1 flex size-3.5 shrink-0 items-center justify-center rounded-full",
                    option.dotClass,
                  )}
                >
                  <Check
                    className="text-background h-2.5 w-2.5"
                    strokeWidth={4}
                  />
                </span>
              ) : (
                <span
                  className={cn(
                    "mt-1 size-3.5 shrink-0 rounded-full transition-colors",
                    "bg-muted",
                    option.hoverDotClass,
                  )}
                />
              )}
              <div className="flex-1">
                <span className="block font-mono text-xs font-semibold tracking-wide uppercase">
                  {option.label}
                </span>
                <span className="text-muted-foreground text-xs">
                  {optionBlocked
                    ? "Enable public access on the tunnel source first to allow anonymous serving."
                    : option.description}
                </span>
              </div>
            </DropdownMenuItem>
          );
        })}
        {publicBlocked && (
          <DropdownMenuItem asChild className="mt-1 border-t">
            <Link
              to={publicAccessHref}
              className="text-muted-foreground hover:text-foreground flex cursor-pointer items-center gap-1 px-2 py-2 text-xs hover:no-underline"
            >
              Enable public access in Settings
              <ArrowRight className="size-3" />
            </Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function MCPServerAvailabilityToggle({
  server,
}: {
  server: McpServer;
}): JSX.Element {
  const { canWrite, updateVisibility, updating } =
    useMcpServerVisibilityUpdate(server);
  const available = server.visibility !== "disabled";

  return (
    <Switch
      checked={available}
      disabled={!canWrite || updating}
      aria-label={available ? "Disable MCP server" : "Enable MCP server"}
      onCheckedChange={(checked) => {
        updateVisibility(checked ? "private" : "disabled");
      }}
    />
  );
}

function visibilityLabel(visibility: McpServerVisibility): string {
  switch (visibility) {
    case "disabled":
      return "Disabled";
    case "public":
      return "Public";
    case "private":
      return "Private";
  }
}

function visibilityToast(visibility: McpServerVisibility): string {
  switch (visibility) {
    case "disabled":
      return "MCP server disabled";
    case "public":
      return "MCP server set to public";
    case "private":
      return "MCP server set to private";
  }
}

function useMcpServerVisibilityUpdate(server: McpServer): {
  canWrite: boolean;
  updateVisibility: (visibility: McpServerVisibility) => void;
  updating: boolean;
} {
  const { hasScope } = useRBAC();
  // These controls offer no public state for hosted servers; their visibility stays on the toolset page.
  const canWrite = hasScope("mcp:write") && !isCanonicalHostedWrapper(server);
  const queryClient = useQueryClient();
  const update = useUpdateMcpServerMutation({
    onSuccess: async (_data, variables) => {
      await Promise.all([
        invalidateAllGetMcpServer(queryClient, { refetchType: "all" }),
        invalidateAllMcpServers(queryClient, { refetchType: "all" }),
        invalidateAllResourceAudience(queryClient, { refetchType: "all" }),
        // Enabling a disabled server (e.g. disabled -> private) auto-attaches
        // it to the Default plugin server-side, which the plugin banner's
        // membership check and publish-freshness state need to pick up.
        invalidateAllPlugins(queryClient, { refetchType: "all" }),
        invalidateAllPublishStatus(queryClient, { refetchType: "all" }),
      ]);
      const next = variables.request.updateMcpServerForm.visibility;
      toast.success(visibilityToast(next));
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update server visibility",
      );
    },
  });

  const updateVisibility = (next: McpServerVisibility) => {
    if (next === server.visibility) return;
    update.mutate({
      request: {
        updateMcpServerForm: {
          id: server.id,
          name: server.name ?? undefined,
          remoteMcpServerId: server.remoteMcpServerId ?? undefined,
          tunneledMcpServerId: server.tunneledMcpServerId ?? undefined,
          toolsetId: server.toolsetId ?? undefined,
          unproxiedMcpServerId: server.unproxiedMcpServerId ?? undefined,
          environmentId: server.environmentId ?? undefined,
          // updateMcpServer is a full-record replace for the optional UUID
          // references. Forwarding them keeps stored values intact across a
          // visibility-only update.
          toolVariationsGroupId: server.toolVariationsGroupId ?? undefined,
          visibility: next,
        },
      },
    });
  };

  return { canWrite, updateVisibility, updating: update.isPending };
}
